import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Accessibility, Volume2, MessageSquare, Swords } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { Switch } from '../components/ui/switch';
import { hasSavedGame } from '../store/persistence';
import audioManager from '../audio/audioManager';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

function Row({ icon: Icon, title, desc, checked, onChange, testId, disabled }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl bg-[#14452F]/60 border border-[#D4AF37]/15 p-3.5 ${
        disabled ? 'opacity-70' : ''
      }`}
    >
      <div className="w-9 h-9 rounded-lg bg-[#2C1A14] flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-[#D4AF37]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans font-semibold text-sm text-[#FDFBF7]">{title}</p>
        {desc && <p className="font-sans text-xs text-[#E0E0D1]/80 mt-0.5">{desc}</p>}
        {disabled && (
          <p className="font-sans text-[0.7rem] text-[#D4AF37]/80 mt-0.5">
            Richiede la modalità accessibile attiva.
          </p>
        )}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testId}
        aria-label={title}
      />
    </div>
  );
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { settings, update } = useSettings();

  // A game is "in progress" while an unfinished game is saved. In that case
  // changing difficulty must not alter the running game — it asks to start a
  // new one instead.
  const activeGame = hasSavedGame();
  const [pendingDiff, setPendingDiff] = useState(null);
  const triggerRef = useRef(null); // button that opened the dialog, for focus return
  const pendingLabel =
    pendingDiff === 'easy' ? 'Facile' : pendingDiff === 'hard' ? 'Difficile' : '';

  const pickDifficulty = (key, el) => {
    if (key === settings.difficulty) return;
    if (activeGame) {
      triggerRef.current = el || null;
      setPendingDiff(key);
    } else {
      update({ difficulty: key });
    }
  };

  const confirmNewGame = () => {
    const key = pendingDiff;
    setPendingDiff(null);
    if (!key) return;
    update({ difficulty: key });
    audioManager.unlock();
    navigate('/game', { state: { mode: 'new' } });
  };

  return (
    <div className="min-h-[100dvh] w-full bg-[#0d2a1c] flex justify-center" data-testid="settings-screen">
      <div className="w-full max-w-md px-5 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="settings-back-btn"
            aria-label="Torna indietro"
            className="w-10 h-10 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-serif text-3xl font-bold text-[#FDFBF7]">Impostazioni</h1>
        </div>

        <div className="mb-3 rounded-xl bg-[#14452F]/60 border border-[#D4AF37]/15 p-3.5" data-testid="difficulty-selector">
          <div className="flex items-center gap-3 mb-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#2C1A14] flex items-center justify-center shrink-0">
              <Swords className="w-4.5 h-4.5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-semibold text-sm text-[#FDFBF7]">Difficoltà avversario</p>
              <p className="font-sans text-xs text-[#E0E0D1]/80 mt-0.5">
                {activeGame
                  ? 'Durante una partita, cambiare difficoltà avvia una nuova partita.'
                  : "Cambia solo il comportamento dell'IA, non le regole del gioco."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Difficoltà avversario">
            {[
              { key: 'easy', label: 'Facile', desc: 'Avversario tranquillo' },
              { key: 'hard', label: 'Difficile', desc: 'Avversario strategico' },
            ].map((opt) => {
              const active = settings.difficulty === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={`${opt.label}. ${opt.desc}.`}
                  disabled={activeGame && active}
                  onClick={(e) => pickDifficulty(opt.key, e.currentTarget)}
                  data-testid={`difficulty-${opt.key}`}
                  className={`rounded-lg py-2.5 font-sans font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/50 disabled:cursor-default ${
                    active
                      ? 'bg-[#D4AF37] text-[#14452F]'
                      : 'bg-[#2C1A14] text-[#FDFBF7] border border-[#D4AF37]/30 hover:bg-[#3D261E]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Row
            icon={Accessibility}
            title="Modalità accessibile"
            desc="Navigazione VoiceOver, telecronaca e selezione prese accessibile."
            checked={settings.accessibleMode}
            onChange={(v) => update({ accessibleMode: v })}
            testId="setting-accessible"
          />
          <Row
            icon={MessageSquare}
            title="Telecronaca"
            desc="Racconta le azioni dell'avversario e gli eventi importanti, tramite VoiceOver."
            checked={settings.narrationEnabled}
            onChange={(v) => update({ narrationEnabled: v })}
            testId="setting-narration"
            disabled={!settings.accessibleMode}
          />
          <Row
            icon={Volume2}
            title="Effetti sonori"
            desc="Suoni discreti per carte, prese e scope."
            checked={settings.soundEnabled}
            onChange={(v) => update({ soundEnabled: v })}
            testId="setting-sound"
          />
        </div>

        <div className="mt-6 rounded-xl bg-[#2C1A14]/70 border border-[#D4AF37]/20 p-4">
          <h2 className="font-serif text-lg text-[#D4AF37] mb-2">
            Come giocare con VoiceOver
          </h2>
          <ul className="font-sans text-xs text-[#E0E0D1] space-y-1.5 list-disc pl-4">
            <li>Scorri per esplorare le carte della tua mano.</li>
            <li>Doppio tap per selezionare una carta.</li>
            <li>
              Se esistono più prese, ti vengono lette tutte: scegline una con
              doppio tap e la presa viene eseguita subito.
            </li>
            <li>Se non ci sono prese, la carta viene giocata sul tavolo.</li>
            <li>Usa il comando “Leggi tavolo” per conoscere le carte in gioco.</li>
            <li>La telecronaca racconta le mosse dell’avversario.</li>
          </ul>
        </div>
      </div>

      <AlertDialog
        open={!!pendingDiff}
        onOpenChange={(o) => {
          if (!o) setPendingDiff(null);
        }}
      >
        <AlertDialogContent
          data-testid="difficulty-confirm-dialog"
          onCloseAutoFocus={(e) => {
            if (triggerRef.current) {
              e.preventDefault();
              triggerRef.current.focus();
            }
          }}
          className="bg-[#14452F] border border-[#D4AF37]/40 text-[#FDFBF7] max-w-sm rounded-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl text-[#FDFBF7]">
              {`Vuoi iniziare una nuova partita con la difficoltà ${pendingLabel}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#E0E0D1]">
              La partita attuale verrà abbandonata e ne inizierà una nuova.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="difficulty-cancel-btn"
              className="bg-[#2C1A14] text-[#FDFBF7] border border-[#D4AF37]/40 hover:bg-[#3D261E] hover:text-[#FDFBF7]"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="difficulty-confirm-btn"
              onClick={confirmNewGame}
              className="bg-[#D4AF37] text-[#14452F] hover:bg-[#F2D675]"
            >
              Conferma
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
